import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_DARK, LOGO_LIGHT, NAV_LINKS } from "@/lib/site";

const testId = (label) => label.toLowerCase().replace(/\s+/g, "-");

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 48);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const light = !scrolled && !open;

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300 ${
                scrolled && !open
                    ? "border-b border-line bg-cream/95 backdrop-blur-md"
                    : "border-b border-transparent bg-transparent"
            }`}
            data-testid="site-header"
        >
            <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <Link to="/" aria-label="ISHAAN WORLDLINK LLP — Home" data-testid="nav-logo">
                    <img
                        src={light ? "/LOGO%20COMPLETE.jpeg" : "/Forest%20Sun%20ISHAAN%20Logo.png"}
                        alt="ISHAAN WORLDLINK LLP"
                        className="h-12 w-auto sm:h-14"
                    />
                </Link>

                <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-${testId(l.label)}`}
                            className={({ isActive }) =>
                                `nav-link text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-200 ${
                                    light
                                        ? "text-cream/85 hover:text-cream"
                                        : "text-ink/75 hover:text-ink"
                                } ${isActive ? "active" : ""}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Link
                        to="/contact"
                        data-testid="nav-enquire-btn"
                        className={`hidden items-center gap-2 px-5 py-2.5 text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 lg:inline-flex ${
                            light
                                ? "bg-cream text-ink hover:bg-teak hover:text-cream"
                                : "bg-ink text-cream hover:bg-teak"
                        }`}
                    >
                        Enquire Now <ArrowUpRight className="h-4 w-4" />
                    </Link>
                    <button
                        type="button"
                        data-testid="nav-menu-toggle"
                        onClick={() => setOpen(!open)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        className={`p-2 lg:hidden ${light ? "text-cream" : "text-ink"}`}
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 top-20 z-40 flex flex-col justify-center bg-ink px-8 lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <nav className="flex flex-col gap-1" aria-label="Mobile">
                            {NAV_LINKS.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.06 + i * 0.06, duration: 0.4 }}
                                >
                                    <NavLink
                                        to={l.to}
                                        end={l.to === "/"}
                                        data-testid={`mobile-nav-${testId(l.label)}`}
                                        className={({ isActive }) =>
                                            `block py-3 font-display text-3xl font-semibold tracking-tight transition-colors ${
                                                isActive ? "text-gold" : "text-cream"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </nav>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.45 }}
                            className="mt-8"
                        >
                            <Link
                                to="/contact"
                                data-testid="mobile-nav-enquire-btn"
                                className="inline-flex items-center gap-2 bg-teak px-6 py-3.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-cream"
                            >
                                Enquire Now <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
